/**
 * useVoiceRecorder — hold-to-record voice notes.
 *
 * Wraps the browser MediaRecorder API. Consumer calls `start()` on
 * pointer-down and `stop()` on pointer-up. `cancel()` aborts without
 * emitting audio (used when the user swipes/moves away).
 *
 * Produces a Blob (audio/webm most browsers, audio/mp4 on Safari).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const MAX_SECONDS = 120; // safety stop after 2 min

function pickMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

export function useVoiceRecorder({ onDone, onError }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const chunks = useRef([]);
  const recorder = useRef(null);
  const stream = useRef(null);
  const startedAt = useRef(0);
  const timer = useRef(null);
  const cancelled = useRef(false);

  const cleanup = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (stream.current) {
      stream.current.getTracks().forEach((t) => t.stop());
      stream.current = null;
    }
    recorder.current = null;
    chunks.current = [];
    setRecording(false);
    setSeconds(0);
  }, []);

  const start = useCallback(async () => {
    if (recording) return;
    cancelled.current = false;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Mikrofon støttes ikke");
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(s, { mimeType: mime }) : new MediaRecorder(s);
      recorder.current = rec;
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => {
        const wasCancelled = cancelled.current;
        const duration = Math.round((Date.now() - startedAt.current));
        const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
        cleanup();
        if (!wasCancelled && blob.size > 500) onDone?.(blob, duration);
      };
      startedAt.current = Date.now();
      rec.start();
      setRecording(true);
      setSeconds(0);
      timer.current = setInterval(() => {
        const s2 = Math.floor((Date.now() - startedAt.current) / 1000);
        setSeconds(s2);
        if (s2 >= MAX_SECONDS) {
          try { rec.stop(); } catch { /* ignore */ }
        }
      }, 200);
    } catch (e) {
      cleanup();
      onError?.(e?.message || "Kunne ikke starte opptak");
    }
  }, [recording, onDone, onError, cleanup]);

  const stop = useCallback(() => {
    if (!recorder.current) return;
    try { recorder.current.stop(); } catch { /* ignore */ }
  }, []);

  const cancel = useCallback(() => {
    if (!recorder.current) return;
    cancelled.current = true;
    try { recorder.current.stop(); } catch { /* ignore */ }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  return { recording, seconds, start, stop, cancel };
}
